import * as React from 'react';

import { enumLoadStatus } from '@rematter/frontend-lib-api-client';
import { cn, ContentCard } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $deductionTypes,
  $loadId,
  $loadReconciliation,
  $resetMaterialsReconciliationFormToFullReconciliation,
} from '../../_state/loadReconciliation.state';
import { useTranslations } from '../../i18n/useTranslations';
import { LoadFormState, LoadRecord, WeightUnitType } from '../../types/load';
import { MaterialsReconciliation } from '../_shared/MaterialsReconciliation';
import { ReconciliationFooter } from '../_shared/MaterialsReconciliation/ReconciliationFooter';

import {
  $defaultUnitSystemUnit,
  $salesDefaultWeightDisplayUnit,
} from '~/shared/state/settings.state';

type LoadReconciliationFormProps = {
  load?: LoadRecord | null;
  isSaving?: boolean;
  isDisabled?: boolean;
  onDiscard?: (onlyDiscardDatesAndEntities?: boolean) => void;
  onSaveUpdates?: (
    tab?: 'load-info' | 'materials',
    data?: LoadFormState,
    onlyDatesAndEntities?: boolean,
  ) => void;
};

/*
 * @description: Once we reach LoadReconciliationForm we should no longer modify loadForm,
 * only used it as readonly information to fetch shipped and received lineItems
 */

export const LoadReconciliationForm: React.FC<LoadReconciliationFormProps> = ({
  load,
  isDisabled = false,
}) => {
  const { data: deductionTypes } = useAtomValue($deductionTypes);
  const { data: loadReconciliation } = useAtomValue($loadReconciliation);

  const facilityDefaultUnit = useAtomValue($defaultUnitSystemUnit);
  const defaultWeightDisplayUnit = useAtomValue($salesDefaultWeightDisplayUnit);

  const t = useTranslations();

  // Initialize materials reconciliation form
  const resetFormToFullReconciliation = useSetAtom(
    $resetMaterialsReconciliationFormToFullReconciliation,
  );

  // Weight unit state - managed internally
  const [weightUnit, setWeightUnit] = React.useState<WeightUnitType>(
    defaultWeightDisplayUnit,
  );

  // Update weight unit when default changes
  React.useEffect(() => {
    setWeightUnit(defaultWeightDisplayUnit);
  }, [defaultWeightDisplayUnit]);

  // Set loadId in the reconciliation atom when load changes
  const setLoadId = useSetAtom($loadId);
  React.useEffect(() => {
    if (load?.id) {
      setLoadId(load.id);
    }
  }, [load?.id, setLoadId]);

  const handleResetForm = React.useCallback(() => {
    if (load && loadReconciliation) {
      const loadForForm = {
        lineItems: load.lineItems ?? [],
        salesOrder: load.salesOrder,
      };
      resetFormToFullReconciliation(loadForForm, loadReconciliation);
    }
    return;
  }, [load, loadReconciliation, resetFormToFullReconciliation]);

  // Initialize form when reconciliation data is loaded
  React.useEffect(() => {
    handleResetForm();
  }, [handleResetForm]);

  if (!load) {
    return null;
  }

  const isLoadReconciled = load.status === enumLoadStatus.Reconciled;

  const isReconciliationDataReady =
    !isLoadReconciled || (isLoadReconciled && loadReconciliation);

  if (!isReconciliationDataReady) {
    return (
      <ContentCard>
        <div className="flex justify-center py-8">
          <div className="text-neutral/60">
            {t.loadDetail.loadingReconciliationData}
          </div>
        </div>
      </ContentCard>
    );
  }

  return (
    <div
      className={cn(
        '@LoadReconciliationForm',
        'overflow-y-auto rounded-lg border border-neutral/10 bg-white',
      )}
    >
      <MaterialsReconciliation
        load={load}
        deductionTypes={deductionTypes ?? []}
        dialogMode={false}
        disabled={isDisabled}
        weightUnit={weightUnit}
        facilityDefaultUnit={facilityDefaultUnit}
        onWeightUnitChange={setWeightUnit}
      />

      <ReconciliationFooter
        onDiscard={handleResetForm}
        weightUnit={weightUnit}
        facilityDefaultUnit={facilityDefaultUnit}
      />
    </div>
  );
};
